import type {
  ChildAvailabilityItem,
  ChildDetails,
} from '@sola/schema/dotcom/children/child.ts';
import type { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import {
  mockChildAvailability,
  mockChildDetails,
} from '@sola/schema/dotcom/children/mocks.ts';
import type {
  GraphQLCommitment,
  GraphQLSupporter,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';

type Participant = NonNullable<GraphQLCommitment['participant']>;

export const MOCK_UNAVAILABLE_REPLACEMENT_CHILD_ID = 'mock-unavailable-child';

const createCandidate = ({
  globalId,
  localId,
  preferredName,
  firstName = preferredName,
  birthDate,
  country,
  gender,
  imageUrl,
  minDaysWaiting,
}: {
  globalId: string;
  localId: string;
  preferredName: string;
  firstName?: string;
  birthDate: string;
  country: string;
  gender: ChildGenderEnum;
  imageUrl: string;
  minDaysWaiting: number;
}) => {
  const details: ChildDetails = {
    ...mockChildDetails,
    globalId,
    localId,
    birthDate,
    country,
    gender,
    imageUrl,
    icp: { country, id: localId.slice(0, 6) },
    name: { first: firstName, preferred: preferredName },
  };
  const availability: ChildAvailabilityItem = {
    ...mockChildAvailability,
    age: new Date().getUTCFullYear() - Number(birthDate.slice(0, 4)),
    birthDate,
    fieldOffice: localId.slice(0, 2),
    gender,
    globalId,
    icp: details.icp,
    imageUrl,
    localId,
    minDaysWaiting,
    name: details.name,
  };

  return { availability, details };
};

export const MOCK_DEPARTURE_SUB_CANDIDATES = [
  createCandidate({
    globalId: '09549200',
    localId: 'CO075900131',
    preferredName: 'Erika',
    firstName: 'Erika Mardeli',
    birthDate: '2013-02-23 00:00:00',
    country: 'Colombia',
    gender: 'Female' as ChildGenderEnum,
    imageUrl:
      'https://media.ci.org/w_600/v1692289081/ChildPhotos/Published/09549200_gxuhss.jpg',
    minDaysWaiting: 142,
  }),
  createCandidate({
    globalId: '11223344',
    localId: 'CO075900132',
    preferredName: 'Mateo',
    birthDate: '2015-08-14 00:00:00',
    country: 'Colombia',
    gender: 'Male' as ChildGenderEnum,
    imageUrl: mockChildDetails.imageUrl ?? '',
    minDaysWaiting: 187,
  }),
  createCandidate({
    globalId: '22334455',
    localId: 'ET051000101',
    preferredName: 'Kalkidan',
    birthDate: '2014-05-09 00:00:00',
    country: 'Ethiopia',
    gender: 'Female' as ChildGenderEnum,
    imageUrl: mockChildDetails.imageUrl ?? '',
    minDaysWaiting: 96,
  }),
  createCandidate({
    globalId: MOCK_UNAVAILABLE_REPLACEMENT_CHILD_ID,
    localId: 'PE064400133',
    preferredName: 'Lucia',
    birthDate: '2016-10-02 00:00:00',
    country: 'Peru',
    gender: 'Female' as ChildGenderEnum,
    imageUrl: mockChildDetails.imageUrl ?? '',
    minDaysWaiting: 210,
  }),
] as const;

const getMockIcpId = (localId?: string | null): string =>
  localId?.slice(0, 6) || 'CO0759';

const mapParticipantToChildDetails = (
  participant: Participant,
): ChildDetails => ({
  ...mockChildDetails,
  birthDate: participant.birthdate
    ? `${participant.birthdate} 00:00:00`
    : mockChildDetails.birthDate,
  country: participant.country ?? mockChildDetails.country,
  gender: (participant.gender as ChildGenderEnum) ?? mockChildDetails.gender,
  globalId: participant.globalId ?? mockChildDetails.globalId,
  imageUrl: participant.photoUrl ?? mockChildDetails.imageUrl,
  icp: {
    country: participant.country ?? mockChildDetails.icp.country,
    id: getMockIcpId(participant.localId),
  },
  localId: participant.localId ?? mockChildDetails.localId,
  name: {
    first: participant.name ?? mockChildDetails.name.first,
    preferred: participant.name ?? mockChildDetails.name.preferred,
  },
});

export const getMockChildDetails = ({
  childId,
  supporter,
}: {
  childId: string;
  supporter: GraphQLSupporter;
}): ChildDetails | null => {
  const participant = supporter.commitments
    ?.map((commitment: GraphQLCommitment) => commitment.participant)
    .find(
      (
        candidate: GraphQLCommitment['participant'],
      ): candidate is Participant => {
        return !!candidate?.globalId && candidate.globalId === childId;
      },
    );

  if (participant) {
    return mapParticipantToChildDetails(participant);
  }

  return (
    MOCK_DEPARTURE_SUB_CANDIDATES.find(
      ({ details }) => details.globalId === childId,
    )?.details ?? null
  );
};

const getAge = (birthDate: string): number => {
  const birthYear = Number.parseInt(birthDate.slice(0, 4), 10);
  return Number.isNaN(birthYear)
    ? (mockChildAvailability.age ?? 0)
    : new Date().getUTCFullYear() - birthYear;
};

export const getMockChildAvailability = ({
  childId,
  supporter,
}: {
  childId: string;
  supporter: GraphQLSupporter;
}): ChildAvailabilityItem | null => {
  const candidate = MOCK_DEPARTURE_SUB_CANDIDATES.find(
    ({ availability }) => availability.globalId === childId,
  );
  if (candidate) {
    return candidate.availability;
  }

  const child = getMockChildDetails({ childId, supporter });

  if (!child) {
    return null;
  }

  return {
    ...mockChildAvailability,
    age: getAge(child.birthDate),
    birthDate: child.birthDate,
    fieldOffice: child.localId.slice(0, 2),
    gender: child.gender,
    globalId: child.globalId,
    icp: child.icp,
    imageUrl: child.imageUrl,
    localId: child.localId,
    name: child.name,
  };
};

export const getMockSameCenterChildAvailability = ({
  centerId,
  excludeChildId,
  limit,
}: {
  centerId: string;
  excludeChildId?: string;
  limit: number;
}): ChildAvailabilityItem[] =>
  MOCK_DEPARTURE_SUB_CANDIDATES.map(({ availability }) => availability)
    .filter(({ icp }) => icp.id === centerId)
    .filter(({ globalId }) => globalId !== excludeChildId)
    .slice(0, limit);
